import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useIntl, defineMessages } from 'react-intl';

import useCompanyBillingStatus from 'components/billing/useCompanyBillingStatus';
import CCTooltip from 'components/shared/DesignSystem/CCFloating/CCTooltip';
import { canAccessFeature, FEATURES } from 'helpers/features';
import useShowTaskListTemplateTooltip from 'components/projects/taskList/hooks/useShowTaskListTemplateTooltip';

import {
  Dropdown,
  DropdownItem,
} from 'components/shared/DesignSystem/Dropdown';
import UpgradeIcon from 'components/shared/DesignSystem/UpgradeIcon';

const TaskListHeaderMenuContainer = styled.div`
  .dropdown-menu {
    width: 225px;
    top: 48px;
  }
`;

const TooltipText = styled.p`
  margin-bottom: 0px;
  width: 240px;
  font-weight: 500;
  font-size: var(--cc_size_text_xs);
`;

const MenuIcon = styled.i`
  display: grid;
  place-items: center;
  cursor: pointer;
  height: var(--cc_size_avatar_l);
  width: var(--cc_size_avatar_l);
  border-radius: var(--cc_size_border_radius_l);
  font-size: var(--cc_size_text_l);
  background-color: var(--cc_color_button_background_subtle);
  color: var(--cc_color_button_text_subtle);
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--cc_color_button_background_subtle_darken_10);
  }
`;

const messages = defineMessages({
  editTaskList: {
    id: 'Projects.taskList.taskListHeader.editTaskList',
    defaultMessage: 'Rename List',
    description: 'drop down menu item for task list',
  },
  exportToPdf: {
    id: 'Projects.taskList.taskListHeader.exportToPdf',
    defaultMessage: 'Export to PDF',
    description: 'dropdown menu item for tasklist',
  },
  reorderTasks: {
    id: 'Projects.taskList.taskListHeader.reorderTasks',
    defaultMessage: 'Reorder Fields',
    description: 'Copy for "reorder tasks" dropdown option',
  },
  saveAsTemplate: {
    id: 'Projects.taskList.taskListHeader.saveAsTemplate',
    defaultMessage: 'Save List as Template',
    description: 'drop down menu item for task list',
  },
  deleteTaskList: {
    id: 'Projects.taskList.taskListHeader.deleteTaskList',
    defaultMessage: 'Delete List',
    description: 'drop down menu item for task list',
  },
  tooltipMsg: {
    id: 'Projects.taskList.taskListHeader.tooltipMsg',
    defaultMessage: 'Save as a template to quickly build future checklists',
    description: 'tooltip message for task list header',
  },
  tooltipUpgradeMsg: {
    id: 'Projects.taskList.taskListHeader.tooltipUpgradeMsg',
    defaultMessage:
      'Upgrade to save as a template to quickly build future checklists',
    description: 'tooltip message for task list header',
  },
});

const TaskListHeaderMenu = ({
  isVisible,
  showEditOptions,
  showReorderSectionsOption,
  showReorderTasksOption,
  showTemplateOption,
  showDeleteOption,
  onEditClick,
  onExportClick,
  onReorderSectionsClick,
  onReorderTasksClick,
  onSaveAsTemplateClick,
  onDeleteClick,
}) => {
  const { formatMessage } = useIntl();

  const { showTaskListTemplateTooltip, onTooltipClose } =
    useShowTaskListTemplateTooltip();

  const { featureSet, loading } = useCompanyBillingStatus();
  const planIncludesTemplates = canAccessFeature(
    featureSet,
    FEATURES.TEMPLATES,
  );

  const templateOptionStyle = !planIncludesTemplates
    ? {
        opacity: 0.4,
        pointerEvents: 'none',
      }
    : null;

  if (!isVisible) {
    return null;
  }
  return (
    <TaskListHeaderMenuContainer>
      <CCTooltip
        position="bottom-end"
        offset={50}
        triggerStyle={
          showTaskListTemplateTooltip && !loading ? 'always' : 'hidden'
        }
        onClickOutside={onTooltipClose}
      >
        <TooltipText>
          {planIncludesTemplates
            ? formatMessage(messages.tooltipMsg)
            : formatMessage(messages.tooltipUpgradeMsg)}
        </TooltipText>
      </CCTooltip>

      <Dropdown
        triggerContent={<MenuIcon className="mdi mdi-dots-horizontal" />}
        style={{ top: '50px', right: '0px', width: 200 }}
        // Allows closing of tooltip on trigger click
        stopPropagationOnTrigger={false}
        triggerTestId="project__item__more-menu-trigger"
      >
        {({ closeMenu }) => {
          return (
            <>
              {showEditOptions ? (
                <>
                  <DropdownItem onClick={onEditClick}>
                    <i className="mdi mdi-pencil-outline" />
                    {formatMessage(messages.editTaskList)}
                  </DropdownItem>
                  <DropdownItem onClick={onExportClick}>
                    <i className="mdi mdi-file-pdf-box" />
                    {formatMessage(messages.exportToPdf)}
                  </DropdownItem>
                </>
              ) : null}

              {showReorderSectionsOption ? (
                <DropdownItem
                  onClick={() => {
                    onReorderSectionsClick();
                    closeMenu();
                  }}
                >
                  <i className="mdi mdi-sort" />
                  {formatMessage({
                    id: 'Projects.taskList.taskListSection.reorderSections',
                  })}
                </DropdownItem>
              ) : null}

              {showReorderTasksOption ? (
                <DropdownItem
                  onClick={() => {
                    onReorderTasksClick();
                    closeMenu();
                  }}
                >
                  <i className="mdi mdi-sort" />
                  {formatMessage(messages.reorderTasks)}
                </DropdownItem>
              ) : null}
              {showTemplateOption ? (
                <DropdownItem
                  onClick={onSaveAsTemplateClick}
                  disabled={!planIncludesTemplates}
                  style={templateOptionStyle}
                >
                  <i className="mdi mdi-text-box-plus-outline " />
                  {formatMessage(messages.saveAsTemplate)}
                  {!planIncludesTemplates ? (
                    <UpgradeIcon
                      size="medium"
                      style={{ marginLeft: 'auto', opacity: 1 }}
                    />
                  ) : null}
                </DropdownItem>
              ) : null}
              {showDeleteOption ? (
                <>
                  <hr />
                  <DropdownItem onClick={onDeleteClick}>
                    <i className="mdi mdi-delete-outline" />
                    {formatMessage(messages.deleteTaskList)}
                  </DropdownItem>
                </>
              ) : null}
            </>
          );
        }}
      </Dropdown>
    </TaskListHeaderMenuContainer>
  );
};

TaskListHeaderMenu.propTypes = {
  isVisible: PropTypes.bool,
  showEditOptions: PropTypes.bool,
  showReorderTasksOption: PropTypes.bool,
  showReorderSectionsOption: PropTypes.bool,
  showTemplateOption: PropTypes.bool,
  showDeleteOption: PropTypes.bool,
  onEditClick: PropTypes.func,
  onExportClick: PropTypes.func,
  onReorderSectionsClick: PropTypes.func,
  onReorderTasksClick: PropTypes.func,
  onSaveAsTemplateClick: PropTypes.func,
  onDeleteClick: PropTypes.func,
};

export default TaskListHeaderMenu;
